/**
 * Card Component - Component Definition
 * =======================================
 *
 * Definición del componente Angular y registro del módulo.
 * Este archivo ensambla todas las partes del componente.
 */

import angular, { IModule, IComponentOptions } from 'angular';
import 'angular-ui-bootstrap';

import { CardController, CardMediaController, CardContentController } from './controller';
import {
  CARD_TEMPLATE,
  CARD_COMPACT_TEMPLATE,
  CARD_MEDIA_TEMPLATE,
  CARD_CONTENT_TEMPLATE,
  CARD_TITLE_TEMPLATE,
  CARD_SUBTITLE_TEMPLATE,
  CARD_TEXT_TEMPLATE,
  CARD_CAPTION_TEMPLATE
} from './template';
import { VARIANTS, SIZES, DEFAULTS, BINDING_NAMES, CardVariant, CardSize, CardLayout } from './constants';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Interfaz para los bindings del componente Card
 */
export interface ICardComponentBindings {
  // Contenido
  title: string;
  subtitle?: string;
  description?: string;
  caption?: string;
  badge?: string;
  imageUrl?: string;
  imageAlt?: string;
  avatar?: string;

  // Apariencia
  variant?: CardVariant;
  size?: CardSize;
  layout?: CardLayout;
  prime?: boolean;
  selected?: boolean;
  elevated?: boolean;
  disabled?: boolean;

  // Acciones
  onClick?: () => void;
  primaryAction?: () => void;
  primaryActionText?: string;
  secondaryAction?: () => void;
  secondaryActionText?: string;
}

// ============================================================================
// DEFINICIÓN DEL COMPONENTE
// ============================================================================

/**
 * Definición del componente ecCard
 *
 * TIPOS DE BINDINGS:
 * - '<'  : One-way binding (padre → hijo) - PREFERIDO
 * - '='  : Two-way binding - EVITAR si es posible
 * - '@'  : String binding (interpolación de texto)
 * - '&?' : Expression binding opcional (callbacks)
 */
export const cardComponentDefinition: IComponentOptions = {
  template: CARD_TEMPLATE,
  controller: CardController,
  transclude: true,
  bindings: {
    // === Contenido ===
    title: '@',           // Título del card (o nombre del deporte en sport-*)
    subtitle: '@',        // Subtítulo opcional
    description: '@',     // Descripción del card
    caption: '@',         // Texto chico extra / caption (variante imagen+contenido y sport-tile)
    badge: '@',           // Texto del badge
    imageUrl: '@',        // URL de imagen header
    imageAlt: '@',        // Alt text de imagen
    avatar: '@',          // URL del avatar circular (variantes sport)

    // === Apariencia ===
    variant: '@',         // Variante de color (layout stack)
    size: '@',            // Tamaño (sm, md, lg)
    layout: '@',          // stack (default) | sport-row | sport-tile
    prime: '<',           // Chip "Precio exclusivo Prime" (variantes sport)
    selected: '<',        // Estado seleccionado (variantes sport)
    elevated: '<',        // Mostrar sombra
    disabled: '<',        // Estado deshabilitado

    // === Acciones (opcionales) ===
    onClick: '&?',
    primaryAction: '&?',
    primaryActionText: '@',
    secondaryAction: '&?',
    secondaryActionText: '@'
  }
};

/**
 * Definición del componente compacto ecCardCompact
 * Versión simplificada para listas
 */
export const cardCompactComponentDefinition: IComponentOptions = {
  template: CARD_COMPACT_TEMPLATE,
  controller: CardController,
  bindings: {
    title: '@',
    subtitle: '@',
    badge: '@',
    imageUrl: '@',
    imageAlt: '@',
    variant: '@',
    elevated: '<',
    disabled: '<',
    onClick: '&?'
  }
};

// ============================================================================
// DEFINICIONES DE HIJOS (Compound Components)
// ============================================================================

/**
 * Definición del componente ecCardMedia
 * Imagen full-bleed; notifica al padre (`registerMedia`) para el padding del content.
 */
export const cardMediaComponentDefinition: IComponentOptions = {
  template: CARD_MEDIA_TEMPLATE,
  controller: CardMediaController,
  require: { card: '^?ecCard' },
  bindings: { src: '@', alt: '@' }
};

/**
 * Definición del componente ecCardContent
 * Bloque de contenido con padding condicionado a `card.compoundHasMedia`.
 */
export const cardContentComponentDefinition: IComponentOptions = {
  template: CARD_CONTENT_TEMPLATE,
  controller: CardContentController,
  require: { card: '^?ecCard' },
  transclude: true
};

/**
 * Definición del componente ecCardTitle
 */
export const cardTitleComponentDefinition: IComponentOptions = {
  template: CARD_TITLE_TEMPLATE,
  transclude: true
};

/**
 * Definición del componente ecCardSubtitle
 */
export const cardSubtitleComponentDefinition: IComponentOptions = {
  template: CARD_SUBTITLE_TEMPLATE,
  transclude: true
};

/**
 * Definición del componente ecCardText
 */
export const cardTextComponentDefinition: IComponentOptions = {
  template: CARD_TEXT_TEMPLATE,
  transclude: true
};

/**
 * Definición del componente ecCardCaption
 */
export const cardCaptionComponentDefinition: IComponentOptions = {
  template: CARD_CAPTION_TEMPLATE,
  transclude: true
};

// ============================================================================
// REGISTRO DEL MÓDULO ANGULAR
// ============================================================================

/**
 * Nombre del módulo Angular
 */
export const MODULE_NAME = 'ec.card';

/**
 * Crear y registrar el módulo
 */
const cardModule: IModule = angular
  .module(MODULE_NAME, ['ui.bootstrap'])
  .component('ecCard', cardComponentDefinition)
  .component('ecCardCompact', cardCompactComponentDefinition)
  .component('ecCardMedia', cardMediaComponentDefinition)
  .component('ecCardContent', cardContentComponentDefinition)
  .component('ecCardTitle', cardTitleComponentDefinition)
  .component('ecCardSubtitle', cardSubtitleComponentDefinition)
  .component('ecCardText', cardTextComponentDefinition)
  .component('ecCardCaption', cardCaptionComponentDefinition);

// ============================================================================
// EXPORTS
// ============================================================================

export {
  CardController,
  CardMediaController,
  CardContentController,
  CARD_TEMPLATE,
  CARD_COMPACT_TEMPLATE,
  CARD_MEDIA_TEMPLATE,
  CARD_CONTENT_TEMPLATE,
  CARD_TITLE_TEMPLATE,
  CARD_SUBTITLE_TEMPLATE,
  CARD_TEXT_TEMPLATE,
  CARD_CAPTION_TEMPLATE,
  VARIANTS,
  SIZES,
  DEFAULTS,
  BINDING_NAMES
};

// Re-export types
export type { CardVariant, CardSize, CardLayout } from './constants';

export default cardModule;
